'use client';

import { useCallback, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { TwoColumnLayout } from '@components/layout/Layouts/TwoColumnLayout';
import { CompanyJobsSidebar } from '@components/features/company-jobs-sidebar/CompanyJobsSidebar';
import { CandidateJobsSidebar } from '@components/features/candidate-jobs-sidebar';
import { JobListContent } from '@components/features/job-list-content';
import { SidebarNavigationProvider, type SidebarView } from '@components/contexts/SidebarNavigationContext';
import { FilterDrawerProvider } from '@components/contexts/FilterDrawerContext';
import { MissionControlBestieChatProvider } from '@components/contexts/MissionControlBestieChatContext';
import { CompanyApplicantProvider, useCompanyApplicant } from '@components/contexts/CompanyApplicantContext';
import { ApplicantListContent } from '@components/features/company-applicant-content';
import { MissionControlHireContent } from '@components/features/job-list-content/MissionControlHireContent';
import { useMissionControlContext } from '@hooks/features/useMissionControlContext';
import { useMcHistory } from '@hooks/history/useMcHistory';
import { useClearHeaderSearchContext } from '@components/contexts/HeaderSearchContext';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { getI18nLinkHref } from '@services/i18nPaths';
import type { JobSearchQueryParams } from '@type/filters';
import {
  McUnifiedProfileQueryParams,
  McUnifiedProfileResult,
} from '@components/templates/employer/applicant-list/flow-mc/types';

const googleApiKey = process.env.GOOGLE_MAPS_CLIENT_API_KEY as string;

interface MissionControlProps {
  locale: Locale;
  isAuthenticated?: boolean;
  bundlesData?: ApiSchema<'OfferBundle'>[];
  /** Server-derived initial context — prevents the candidate->company flash for company users. */
  initialContext?: import('@hooks/features/useMissionControlContext').MissionControlContext;
  /** Job slug pre-loaded from the URL (company bookmark navigation). */
  initialSlug?: string;
  /** Applicant status filter pre-loaded from the URL. */
  initialStatus?: number | null;
  /** Initial sidebar view — set by the server page from the current URL so the correct view is shown on first render. */
  initialView?: SidebarView;
  /** SSR-fetched job list data — forwarded to JobListContent so SWR can use it as fallback and the backend receives auth headers on first load. */
  serverSideJobData?: ApiSchema<'CursorJobResult'>;
  /** SSR-fetched MC unified profile list for the applicants status/grid page. */
  serverSideMcUnifiedProfileData?: McUnifiedProfileResult;
  /** MC unified profile filters parsed from the applicants URL. */
  initialMcUnifiedProfileQueryParams?: McUnifiedProfileQueryParams;
}

/** Dispatches close events for sidebar and detail modal on mobile. */
const dispatchMobileClose = () => {
  if (typeof window !== 'undefined' && window.innerWidth < 1024) {
    window.dispatchEvent(new Event('close-sidebar'));
    window.dispatchEvent(new Event('close-detail-modal'));
  }
};

/**
 * Inner company layout — separate component so it can call useCompanyApplicant(),
 * which requires CompanyApplicantProvider to be in the tree above it.
 */
const CompanyLayout = ({
  locale,
  isAuthenticated,
  switchContext,
  bundlesData,
  serverSideMcUnifiedProfileData,
  initialMcUnifiedProfileQueryParams,
}: {
  locale: Locale;
  isAuthenticated: boolean;
  switchContext: () => void;
  bundlesData?: ApiSchema<'OfferBundle'>[];
  serverSideMcUnifiedProfileData?: McUnifiedProfileResult;
  initialMcUnifiedProfileQueryParams?: McUnifiedProfileQueryParams;
}) => {
  const {
    setSelectedJob,
    setInitialStatus,
    selectedJob,
    initialSlug,
  } = useCompanyApplicant();
  const { router } = useExtendedRouter();

  const handleTagClick = useCallback((job: ApiSchema<'McMyJobCard'>, status: number) => {
    dispatchMobileClose();
    setInitialStatus(status);
    setSelectedJob(job);
  }, [setInitialStatus, setSelectedJob]);

  const handleSwitchContext = useCallback(() => {
    dispatchMobileClose();
    switchContext();
  }, [switchContext]);

  // Card click -> open the applicant flow board for this job.
  // Status tag clicks (handleTagClick above) keep opening the rank list with a filter.
  const handleJobSelect = useCallback((job: ApiSchema<'McMyJobCard'>) => {
    dispatchMobileClose();
    void router.push(`/mc/job/${job.slug}/applicants/flow`);
  }, [router]);

  const selectedJobSlug = selectedJob?.slug ?? initialSlug ?? undefined;

  // When no job is selected, show the hire content. When a job is selected, show its applicants.
  // ApplicantListContent renders its own bottom CompanyFloatingNavBar, so hide the general
  // one here to avoid duplicating it when viewing applicants.
  const mainContent = (
    <div className="flex h-full flex-col bg-surface-100">
      <div className="min-h-0 flex-1">
        {selectedJobSlug
          ? (
            <ApplicantListContent
              locale={locale}
              isAuthenticated={isAuthenticated}
              initialMcUnifiedProfileQueryParams={initialMcUnifiedProfileQueryParams}
              serverSideUnifiedProfiles={serverSideMcUnifiedProfileData}
            />
          )
          : <MissionControlHireContent locale={locale} isAuth={isAuthenticated} bundlesData={bundlesData} />}
      </div>
    </div>
  );

  return (
    <TwoColumnLayout
      leftSidebar={(
        <CompanyJobsSidebar
          locale={locale}
          isAuthenticated={isAuthenticated}
          onSwitchContext={handleSwitchContext}
          onJobSelect={handleJobSelect}
          onTagClick={handleTagClick}
          selectedJobSlug={selectedJobSlug}
        />
      )}
      mainContent={mainContent}
      rightSidebar={undefined}
    />
  );
};

/**
 * @description Mission Control - Unified job management hub with two-column layout.
 *
 * Candidate Context:
 * - Left sidebar: Job search alerts with navigation
 * - Main content: Job list content based on sidebar selection
 *
 * Company Context:
 * - Left sidebar: Company jobs sidebar
 * - Main content: Hire content, applicant rank view, or dedicated flow page depending on navigation
 */
export const MissionControl = (props: MissionControlProps) => {
  const {
    locale,
    initialContext,
    isAuthenticated = false,
    bundlesData,
    initialSlug,
    initialStatus,
    initialView,
    serverSideJobData,
    serverSideMcUnifiedProfileData,
    initialMcUnifiedProfileQueryParams,
  } = props;

  const { t } = useTranslation('common');
  const { currentContext, setContext } = useMissionControlContext({ initialContext });
  const clearHeaderSearch = useClearHeaderSearchContext();
  const { push } = useMcHistory(locale);
  const effectiveContext = initialSlug ? 'company' : currentContext;

  // Reset URL to /mc base when switching context so residual search/alert URLs are cleared.
  const handleSwitchContext = useCallback(() => {
    const nextContext = effectiveContext === 'company' ? 'candidate' : 'company';
    setContext(nextContext);
    push(getI18nLinkHref('/mc', locale), {});
  }, [effectiveContext, locale, push, setContext]);

  // Opens the Jobs on Map modal. Lives here (outside the import cycle) so sidebar/card
  // components can invoke it via a callback without creating circular dependencies.
  const handleOpenMap = useCallback((queryParams?: Partial<JobSearchQueryParams>) => {
    if (!googleApiKey) return;

    void (async () => {
      const { showJobsMapModal } = await import('@components/templates/job/list/JobsMapModal');
      showJobsMapModal({ googleApiKey, locale, queryParams });
    })();
  }, [locale]);

  useEffect(() => {
    clearHeaderSearch();
  }, [effectiveContext, clearHeaderSearch]);

  useEffect(() => {
    const title = effectiveContext === 'company'
      ? t('bestie.hire.meta.title', 'BestJobs Hire – Candidați intervievați, rezultate garantate')
      : t('homepage.react.meta.title', 'BestJobs');
    const description = effectiveContext === 'company'
      ? t('bestie.hire.meta.description', 'Bestie este agentul tău AI de recrutare.')
      : t('meta.homepage.description_react', 'BestJobs – Stop searching, start interviewing!');

    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', description);
    }
  }, [effectiveContext, t]);

  if (effectiveContext === 'company') {
    return (
      <CompanyApplicantProvider initialSlug={initialSlug} initialStatus={initialStatus}>
        <CompanyLayout
          locale={locale}
          isAuthenticated={isAuthenticated}
          switchContext={handleSwitchContext}
          bundlesData={bundlesData}
          serverSideMcUnifiedProfileData={serverSideMcUnifiedProfileData}
          initialMcUnifiedProfileQueryParams={initialMcUnifiedProfileQueryParams}
        />
      </CompanyApplicantProvider>
    );
  }

  return (
    <SidebarNavigationProvider initialView={initialView ?? 'new-search'} locale={locale}>
      <FilterDrawerProvider>
        <MissionControlBestieChatProvider>
          <TwoColumnLayout
            leftSidebar={(
              <CandidateJobsSidebar
                locale={locale}
                isAuthenticated={isAuthenticated}
                onSwitchContext={() => {
                  dispatchMobileClose();
                  handleSwitchContext();
                }}
                onOpenMap={handleOpenMap}
              />
            )}
            mainContent={(
              <JobListContent
                locale={locale}
                isAuthenticated={isAuthenticated}
                serverSideJobData={serverSideJobData}
              />
            )}
            rightSidebar={undefined}
          />
        </MissionControlBestieChatProvider>
      </FilterDrawerProvider>
    </SidebarNavigationProvider>
  );
};
